import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { CharlieTable } from '@atlassian/charlie-table/compiled';
import type { ColumnDef } from '@atlassian/charlie-table/tanstack-helpers';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	table: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
	headerCell: {
		font: token('font.heading.xxsmall'),
	},
	rightAlignedCell: {
		display: 'flex',
		justifyContent: 'flex-end',
		width: '100%',
	},
});

const i18n = defineMessages({
	contentTitleHeader: {
		id: 'performance-insights.content-metrics.table.header.content-title.non-final',
		defaultMessage: 'Content title',
		description:
			'Column header in the Content Metrics table located in the first column for the Content title. Labels the column that shows the title of each content item (e.g. page name). Read-only. Translate as the heading for a list of content titles.',
	},
	loadTimeRatingHeader: {
		id: 'performance-insights.content-metrics.table.header.load-time-rating.non-final',
		defaultMessage: 'Load time rating',
		description:
			'Table column header in the Content Metrics table for Load time rating. Labels the column that shows a performance rating (Fast / Moderate / Slow) for how quickly the content loads; values appear as coloured lozenges. Read-only. Translate as the heading for that rating column.',
	},
	medianLoadTimeHeader: {
		id: 'performance-insights.content-metrics.table.header.median-load-time.non-final',
		defaultMessage: 'Median load time',
		description:
			'Table column header in the Content Metrics table for Median load time. Labels the column that shows the median time (in seconds) it takes to load the content; cell values are numbers followed by "s" (e.g. "3.2s"). Read-only. Translate as the heading for that duration column.',
	},
	viewsHeader: {
		id: 'performance-insights.content-metrics.table.header.views.non-final',
		defaultMessage: 'Views',
		description:
			'Table column header in the Content Metrics table for Views. Labels the column that shows how many times the content was viewed (a count). Read-only. Translate as the heading for the view-count column; keep short (single word or short phrase where natural).',
	},
	spaceHeader: {
		id: 'performance-insights.content-metrics.table.header.space.non-final',
		defaultMessage: 'Space',
		description:
			'Table column header in the Content Metrics table for Space. Labels the column that shows the Confluence space the content belongs to. Translate as the heading for the space name column; "Space" here means a Confluence space (container for pages).',
	},
	contentOwnerHeader: {
		id: 'performance-insights.content-metrics.table.header.content-owner.non-final',
		defaultMessage: 'Content owner',
		description:
			'Table column header in the Content Metrics table for Content owner. Labels the column that shows who is responsible for or owns the content (user or role). Translate as the heading for the owner column.',
	},
	medianLoadTimeSeconds: {
		id: 'performance-insights.content-metrics.table.cell.median-load-time-seconds.non-final',
		defaultMessage: '{value}s',
		description:
			'Location: table cell in the Median load time column. UI element: numeric duration. Purpose: shows load time in seconds. Placeholder: {value} is the numeric duration (e.g. 3.2). The "s" is the seconds unit; translate or keep as appropriate for your locale (e.g. "s", " sec", or full word for seconds).',
	},
});

// TODO: update these while working through each column
export interface ContentMetricRow {
	id: string;
	contentTitle: string;
	loadTimeRating: LoadTimeRating;
	medianLoadTime: string;
	views: number;
	space: string;
	contentOwner: string;
}

export interface ContentMetricsTableProps {
	/** List of content metric rows to display. Empty array shows empty state. */
	items?: ContentMetricRow[];
}

// TODO: update this after API structure and types are finalized
export enum LoadTimeRating {
	// Load time > 4 seconds
	SLOW = 'SLOW',
	// Load time between 3.7s and 4s
	MODERATE = 'MODERATE',
	// Load time < 3.7 seconds
	FAST = 'FAST',
}

const LOAD_TIME_RATING_APPEARANCE: Record<LoadTimeRating, 'success' | 'warning' | 'danger'> = {
	[LoadTimeRating.FAST]: 'success',
	[LoadTimeRating.MODERATE]: 'warning',
	[LoadTimeRating.SLOW]: 'danger',
};

export const ContentMetricsTable = ({ items }: ContentMetricsTableProps) => {
	const { formatMessage } = useIntl();

	const columns = useMemo<ColumnDef<ContentMetricRow>[]>(
		() => [
			{
				id: 'contentTitle',
				accessorKey: 'contentTitle',
				header: formatMessage(i18n.contentTitleHeader),
				enableSorting: false,
				cell: ({ row }) => row.original.contentTitle,
				size: 212,
			},
			{
				id: 'loadTimeRating',
				accessorKey: 'loadTimeRating',
				header: formatMessage(i18n.loadTimeRatingHeader),
				enableSorting: false,
				cell: ({ row }) => (
					<Lozenge
						appearance={LOAD_TIME_RATING_APPEARANCE[row.original.loadTimeRating as LoadTimeRating]}
						isBold
					>
						{row.original.loadTimeRating}
					</Lozenge>
				),
				size: 124,
			},
			{
				id: 'medianLoadTime',
				accessorKey: 'medianLoadTime',
				header: formatMessage(i18n.medianLoadTimeHeader),
				enableSorting: false,
				cell: ({ row }) => (
					<Box xcss={styles.rightAlignedCell}>
						{formatMessage(i18n.medianLoadTimeSeconds, {
							value: row.original.medianLoadTime,
						})}
					</Box>
				),
				size: 135,
			},
			{
				id: 'views',
				accessorKey: 'views',
				header: formatMessage(i18n.viewsHeader),
				enableSorting: false,
				cell: ({ row }) => <Box xcss={styles.rightAlignedCell}>{row.original.views}</Box>,
				size: 95,
			},
			{
				id: 'space',
				accessorKey: 'space',
				header: formatMessage(i18n.spaceHeader),
				enableSorting: false,
				cell: ({ row }) => row.original.space,
				size: 165,
			},
			{
				id: 'contentOwner',
				accessorKey: 'contentOwner',
				header: formatMessage(i18n.contentOwnerHeader),
				enableSorting: false,
				cell: ({ row }) => row.original.contentOwner,
				size: 150,
			},
		],
		[formatMessage],
	);

	if (items && items.length === 0) {
		return (
			<Box xcss={styles.table} padding="space.400">
				{/* TODO: add empty state */}
			</Box>
		);
	}

	return (
		<Box xcss={styles.table}>
			<CharlieTable<ContentMetricRow>
				columnDefs={columns}
				data={items}
				headerCellXcss={styles.headerCell}
			/>
		</Box>
	);
};
